import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import axios from 'axios';
import { MainAppReducer } from 'Types';
import { useSelector, useDispatch } from 'react-redux';
import { userDataRequest, getSubscriptionStatus } from 'Actions/app';
import { CustomCircularProgress } from 'Components/StyledComponents';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const r = (error as { response?: { data?: { message?: string } } }).response;
    if (r?.data?.message) return r.data.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

const Cancel = () => {
  enum Mode {
    CANCEL = 'cancel',
    DELETE = 'delete',
  }
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>(Mode.CANCEL);
  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [dialogMessage, setDialogMessage] = useState<string>('');

  const dispatch = useDispatch();
  const subscriptionStatus = useSelector(
    (state: MainAppReducer) => state.mainAppReducer.userData?.subscription_status,
  );
  const isLapseScheduled = ['pending_cancellation'].includes(subscriptionStatus || '');

  const { currentAuthToken, logOut } = useAuthWrapperContext();

  const cancelSubscription = useCallback(async () => {
    try {
      setStatus('pending');
      setDialogMessage('');
      const token = await currentAuthToken();
      const result = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/stripe/cancel-subscription`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      const endDateIso: string | undefined = result?.data?.subscriptionEndDate;
      if (endDateIso) {
        const date = new Date(endDateIso);
        const formatted = isNaN(date.getTime())
          ? endDateIso
          : date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
        console.log('dialogue?');
        setDialogMessage(`Your subscription will remain active until ${formatted}.`);
      } else {
        setDialogMessage(
          'Your subscription has been scheduled to lapse at the end of the current billing period.',
        );
      }
      setStatus('success');
      // refresh user data/subscription status
      dispatch(userDataRequest());
      dispatch(getSubscriptionStatus());
    } catch (error: unknown) {
      setStatus('error');
      setDialogMessage(
        getErrorMessage(
          error,
          'There was an error cancelling your subscription. Please try again.',
        ),
      );
    }
  }, [currentAuthToken, dispatch]);

  const deleteAccount = useCallback(async () => {
    try {
      setStatus('pending');
      setDialogMessage('');
      const token = await currentAuthToken();
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/v1/user-data/delete-account`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setDialogMessage(
        `Your account has been marked for deletion. You will be logged out in 5 seconds.`,
      );
      setStatus('success');
      // refresh user data/subscription status
      dispatch(userDataRequest());
      dispatch(getSubscriptionStatus());
      setTimeout(() => {
        logOut();
      }, 5000);
    } catch (error: unknown) {
      setStatus('error');
      setDialogMessage(
        getErrorMessage(
          error,
          'There was an error cancelling your subscription. Please try again.',
        ),
      );
    }
  }, [currentAuthToken, dispatch]);

  const uncancelSubscription = useCallback(async () => {
    try {
      setStatus('pending');
      setDialogMessage('');
      const token = await currentAuthToken();
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/stripe/uncancel-subscription`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setDialogMessage('Your subscription has been successfully re-activated.');
      setStatus('success');
      // refresh user data/subscription status
      dispatch(userDataRequest());
      dispatch(getSubscriptionStatus());
    } catch (error: unknown) {
      setStatus('error');
      setDialogMessage(
        getErrorMessage(
          error,
          'There was an error re-activating your subscription. Please try again.',
        ),
      );
    }
  }, [currentAuthToken, dispatch]);

  const handleClick = (newMode: Mode) => {
    setMode(newMode);
    setStatus('idle');
    setDialogMessage('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDialogMessage('');
  };
  useEffect(() => {
    if (!open) {
      setStatus('idle');
    }
  }, [open]);
  const handleConfirm = async () => {
    if (mode === Mode.CANCEL) {
      if (isLapseScheduled) {
        await uncancelSubscription();
      } else {
        await cancelSubscription();
      }
    } else {
      await deleteAccount();
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: 4, marginBottom: 2 }}>
        <Button
          variant="text"
          onClick={() => handleClick(Mode.CANCEL)}
          sx={{
            color: '#888',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            fontWeight: 700,
            textTransform: 'uppercase',
            borderRadius: '6px',
            px: 3,
            py: 1.5,
            textDecoration: 'underline',
            textUnderlineOffset: '4px',
            '&:hover': {
              color: '#666',
              backgroundColor: 'transparent',
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
            },
          }}
        >
          {isLapseScheduled ? 'Re-activate Subscription' : 'Cancel Subscription'}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleClick(Mode.DELETE)}
          sx={{
            backgroundColor: '#C24444',
            color: '#fff',
            boxShadow: 'none',
            fontWeight: 700,
            textTransform: 'uppercase',
            borderRadius: '6px',
            px: 3,
            py: 1.5,
            '&:hover': { backgroundColor: '#A83232' },
          }}
        >
          Delete Account
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'center',
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          {status === 'idle' && (
            <>
              {mode === Mode.CANCEL
                ? isLapseScheduled
                  ? 'Re-activate Subscription'
                  : 'Cancel Subscription'
                : 'Delete Account'}
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {status === 'pending' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '40px',
              }}
            >
              <CustomCircularProgress />
            </Box>
          )}
          {mode === Mode.DELETE && (
            <Typography
              sx={{
                textAlign: 'center',
                color: status === 'error' ? '#C24444' : '#888',
                fontWeight: 600,
              }}
            >
              {dialogMessage}
            </Typography>
          )}
          {mode === Mode.CANCEL ? (
            status === 'idle' ? (
              <Typography sx={{ textAlign: 'center', color: '#888', fontWeight: 600 }}>
                {isLapseScheduled
                  ? 'Are you sure you want to re-activate your subscription?'
                  : 'Are you sure you want to cancel your subscription?'}
              </Typography>
            ) : status === 'pending' ? (
              <Typography sx={{ textAlign: 'center', color: '#888', fontWeight: 600 }}>
                {isLapseScheduled
                  ? 'Re-activating your subscription...'
                  : 'Cancelling your subscription...'}
              </Typography>
            ) : (
              <Typography
                sx={{
                  textAlign: 'center',
                  color: status === 'error' ? '#C24444' : '#888',
                  fontWeight: 600,
                }}
              >
                {status === 'success' && dialogMessage}
                {status === 'error' && 'Error, please try again later.'}
              </Typography>
            )
          ) : (
            <Typography sx={{ textAlign: 'center', color: '#888', fontWeight: 600 }}>
              {status === 'idle' &&
                'Are you sure you want to delete your account? This action cannot be undone. Once initiated, you will be logged out and your account will be marked for deletion.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1, justifyContent: 'center' }}>
          {mode === Mode.CANCEL ? (
            status === 'idle' ? (
              <>
                <Button
                  onClick={handleClose}
                  variant="text"
                  sx={{
                    color: '#888',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    borderRadius: '6px',
                    px: 3,
                    py: 1.5,
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                    '&:hover': {
                      color: '#666',
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                      textUnderlineOffset: '4px',
                    },
                  }}
                >
                  No
                </Button>
                <Button
                  onClick={handleConfirm}
                  variant="contained"
                  disabled={status === 'pending'}
                  sx={{
                    backgroundColor: '#C24444',
                    color: '#fff',
                    boxShadow: 'none',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    borderRadius: '6px',
                    px: 3,
                    py: 1.5,
                    '&:hover': { backgroundColor: '#A83232' },
                  }}
                >
                  Yes, {isLapseScheduled ? 'Re-activate' : 'Cancel'} Subscription
                </Button>
              </>
            ) : (
              <Button
                onClick={handleClose}
                variant="contained"
                sx={{
                  backgroundColor: '#469F6A',
                  color: '#fff',
                  boxShadow: 'none',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  borderRadius: '6px',
                  px: 3,
                  py: 1.5,
                  '&:hover': { backgroundColor: '#3b8a5a' },
                }}
              >
                Close
              </Button>
            )
          ) : (
            <>
              {status === 'idle' && (
                <>
                  <Button
                    onClick={handleClose}
                    variant="text"
                    sx={{
                      color: '#888',
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      borderRadius: '6px',
                      px: 3,
                      py: 1.5,
                      textDecoration: 'underline',
                      textUnderlineOffset: '4px',
                      '&:hover': {
                        color: '#666',
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                        textUnderlineOffset: '4px',
                      },
                    }}
                  >
                    No
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    variant="contained"
                    sx={{
                      backgroundColor: '#C24444',
                      color: '#fff',
                      boxShadow: 'none',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      borderRadius: '6px',
                      px: 3,
                      py: 1.5,
                      '&:hover': { backgroundColor: '#A83232' },
                    }}
                  >
                    Yes, Delete
                  </Button>
                </>
              )}
              {status === 'success' && (
                <Button
                  onClick={handleClose}
                  variant="contained"
                  sx={{
                    backgroundColor: '#469F6A',
                    color: '#fff',
                    boxShadow: 'none',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    borderRadius: '6px',
                    px: 3,
                    py: 1.5,
                    '&:hover': { backgroundColor: '#3b8a5a' },
                  }}
                >
                  Close
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Cancel;
